import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COMMISSION_BASIS_DEFS,
  COMMISSION_BASIS_MAP,
  COMMISSION_TARGET_POLICY_DEFS,
  COMMISSION_TARGET_POLICY_MAP,
  type CommissionBasis,
  type CommissionTargetPolicy,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { useTenantProfile } from '../../lib/settings';
import { PageHead } from '../../components/ui';

type DiscountEnforcement = 'OFF' | 'WARN' | 'BLOCK';

const DISCOUNT_ENFORCEMENT: Array<{ key: DiscountEnforcement; label: string; hint: string }> = [
  { key: 'OFF', label: 'غير مُفعَّل', hint: 'لا رصيد يُتتبَّع، والخصومات بلا حدّ.' },
  {
    key: 'WARN',
    label: 'تنبيه فقط',
    hint: 'يُتتبَّع الرصيد ويظهر التجاوز، لكن الإرسال لا يُمنع.',
  },
  {
    key: 'BLOCK',
    label: 'إلزام',
    hint: 'يُمنع إرسال عرضٍ يتجاوز خصمُه رصيدَ المندوب المتبقّي — إلا أن يرسله مدير.',
  },
];

/**
 * إعدادات العمولات — القواعد الافتراضية للاحتساب.
 *
 * نُقلت هنا من شاشة الكشوف: الضبط قرارٌ يُتَّخذ مرة ويثبت، لا يُكرَّر مع كل
 * كشف، فموضعه في الإعدادات مع بقية سياسات الشركة أصدق من صفحة عملٍ يومي.
 * وشاشة الكشوف تبقى تعرض القاعدة السارية وتربط هنا لتعديلها.
 *
 * وهي افتراضات فقط: ما يُحفظ هنا يُنسخ في الكشف لحظة إنشائه، فتغييره بعدُ
 * لا يمسّ كشفًا محسوبًا ولا عمولةً صُرفت.
 */
export function CommissionSettings() {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');

  const [basis, setBasis] = useState<CommissionBasis>('COLLECTED');
  const [policy, setPolicy] = useState<CommissionTargetPolicy>('NONE');
  const [rate, setRate] = useState('0');
  const [discEnf, setDiscEnf] = useState<DiscountEnforcement>('OFF');
  const [discBudget, setDiscBudget] = useState('0');
  const [tgYear, setTgYear] = useState('0');
  const [tgMonth, setTgMonth] = useState('0');
  const [tgWeek, setTgWeek] = useState('0');
  const [dirty, setDirty] = useState(false);

  /* تُملأ من الخادم مرة واحدة: المزامنة الدائمة تمحو ما يكتبه المستخدم. */
  useEffect(() => {
    const s = profile.data?.settings;
    if (!s || dirty) return;
    setBasis((s.commissionBasis as CommissionBasis) ?? 'COLLECTED');
    setPolicy((s.commissionTargetPolicy as CommissionTargetPolicy) ?? 'NONE');
    setRate(String(s.commissionDefaultRate ?? 0));
    setDiscEnf((s.discountEnforcement as DiscountEnforcement) ?? 'OFF');
    setDiscBudget(String(s.discountBudgetDefault ?? 0));
    setTgYear(String(s.salesTargetYear ?? 0));
    setTgMonth(String(s.salesTargetMonth ?? 0));
    setTgWeek(String(s.salesTargetWeek ?? 0));
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/preferences', {
        method: 'PATCH',
        body: {
          commissionBasis: basis,
          commissionTargetPolicy: policy,
          commissionDefaultRate: Number(rate) || 0,
          discountEnforcement: discEnf,
          discountBudgetDefault: Number(discBudget) || 0,
          salesTargetYear: Number(tgYear) || 0,
          salesTargetMonth: Number(tgMonth) || 0,
          salesTargetWeek: Number(tgWeek) || 0,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDirty(false);
    },
  });

  return (
    <div className="page">
      <PageHead
        title="إعدادات المبيعات"
        subtitle="قواعد احتساب العمولات وسياسة رصيد الخصومات"
        actions={
          <>
            <Link to="/settings/sales-targets" className="btn">
              الأهداف والخصومات
            </Link>
            <Link to="/sales/commissions" className="btn">
              كشوف العمولات
            </Link>
          </>
        }
      />

      {/*
        الأهدافُ البيعيّة — ثلاثةٌ لا واحد.

        السنويُّ يُقاس عليه اتجاهُ الشركة، والشهريُّ إيقاعُ الفريق،
        والأسبوعيُّ نبضُه. وقسمةُ السنويّ على اثني عشر لا تُغني: لا الشهورُ
        سواءٌ في البيع ولا الأسابيع — رمضانُ غيرُ سبتمبر، وأسبوعُ العيد
        غيرُ ما بعده. فيُكتب لكلٍّ رقمُه، ويُقاس عليه في قمع المبيعات.

        وصفرٌ يعني «لا هدف» لا «هدفٌ صفر»: فلا تُعرض نسبةُ إنجازٍ إلى صفر،
        ولا يُقال لمن لم يضع هدفًا إنّه قصّر عنه.
      */}
      <div className="card mb-4">
        <div className="card-head">
          <h2 className="card-title">الأهداف البيعية</h2>
          <Link to="/sales/pipeline" className="link small">
            قمع المبيعات
          </Link>
        </div>
        <div className="card-body">
          <p className="tiny mute mb-3">
            قيمةُ الصفقات الفائزة المستهدَفة لكلّ فترة. تُقاس عليها النتائجُ في
            قمع المبيعات، ويُترك صفرًا ما لا هدف له.
          </p>
          <div className="form-grid">
            {(
              [
                ['tg-year', 'الهدف السنوي', tgYear, setTgYear],
                ['tg-month', 'الهدف الشهري', tgMonth, setTgMonth],
                ['tg-week', 'الهدف الأسبوعي', tgWeek, setTgWeek],
              ] as const
            ).map(([id, label, value, set]) => (
              <div className="field" key={id}>
                <label className="label" htmlFor={id}>
                  {label}
                </label>
                <input
                  id={id}
                  className="input num"
                  type="number"
                  min={0}
                  step={1000}
                  value={value}
                  disabled={!canEdit}
                  onChange={(e) => {
                    set(e.target.value);
                    setDirty(true);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">قواعد الاحتساب الافتراضية</h2>
          {canEdit && (
            <button
              className="btn btn-primary"
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          )}
        </div>

        <div className="card-body">
          {!canEdit && (
            <div className="alert alert-info mb-4">
              للعرض فقط — تعديل قواعد العمولات من صلاحية المدير.
            </div>
          )}
          {save.isError && (
            <div className="alert alert-danger mb-4">
              {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
            </div>
          )}
          {save.isSuccess && !dirty && (
            <div className="alert alert-success mb-4">حُفظت القواعد.</div>
          )}

          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="d-basis">
                أساس الاحتساب
              </label>
              <select
                id="d-basis"
                className="select"
                value={basis}
                disabled={!canEdit}
                onChange={(e) => {
                  setBasis(e.target.value as CommissionBasis);
                  setDirty(true);
                }}
              >
                {COMMISSION_BASIS_DEFS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">{COMMISSION_BASIS_MAP[basis].description}</span>
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="d-policy">
                سياسة الهدف
              </label>
              <select
                id="d-policy"
                className="select"
                value={policy}
                disabled={!canEdit}
                onChange={(e) => {
                  setPolicy(e.target.value as CommissionTargetPolicy);
                  setDirty(true);
                }}
              >
                {COMMISSION_TARGET_POLICY_DEFS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">
                {COMMISSION_TARGET_POLICY_MAP[policy].description}
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="d-rate">
                النسبة الافتراضية
              </label>
              <input
                id="d-rate"
                className="input num"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={rate}
                disabled={!canEdit}
                onChange={(e) => {
                  setRate(e.target.value);
                  setDirty(true);
                }}
              />
              <span className="tiny mute">
                تُطبَّق على من لم تُحدَّد له نسبة في ملفه. النسبة في الملف تسبقها دائمًا.
              </span>
            </div>
          </div>

          <div className="alert alert-info mt-4">
            الهدف والنسبة لكل مندوب يُضبطان في ملفه بشؤون الموظفين، ولا يُنسب إليه شيء قبل
            ربط ملفه بحساب مستخدم.
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">سياسة رصيد الخصومات</h2>
        </div>

        <div className="card-body">
          <p className="mute mb-4">
            رصيدٌ شهريّ لما يمنحه المندوب من خصومٍ في عروضه، يُحسب على العروض المرسَلة
            والمقبولة. غرضه ألّا يتصرّف المندوب في هامش الربح بلا حدّ. يُضبط الرصيد الخاص لكل
            مندوب في ملفه بشؤون الموظفين، ومن لا رصيد خاص له يأخذ الرصيد الافتراضي أدناه.
          </p>

          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="disc-enf">
                الإلزام
              </label>
              <select
                id="disc-enf"
                className="select"
                value={discEnf}
                disabled={!canEdit}
                onChange={(e) => {
                  setDiscEnf(e.target.value as DiscountEnforcement);
                  setDirty(true);
                }}
              >
                {DISCOUNT_ENFORCEMENT.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span className="tiny mute">
                {DISCOUNT_ENFORCEMENT.find((d) => d.key === discEnf)?.hint}
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="disc-budget">
                الرصيد الشهري الافتراضي
              </label>
              <input
                id="disc-budget"
                className="input num"
                type="number"
                min="0"
                step="any"
                value={discBudget}
                disabled={!canEdit}
                onChange={(e) => {
                  setDiscBudget(e.target.value);
                  setDirty(true);
                }}
              />
              <span className="tiny mute">صفرٌ يعني ألّا رصيد افتراضي — لا حدّ إلا لمن ضُبط له رصيد خاص.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
